import { z } from "zod";
import { cursorPageSchema, environmentSchema } from "./context.js";
export const fiscalResearchTaskTypeSchema = z.literal("FISCAL_SOURCE_RESEARCH");
export const fiscalResearchTaskStateSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "WAITING_REVIEW",
    "SUCCEEDED",
    "FAILED",
    "CANCELLED",
]);
export const fiscalResearchDispatchStateSchema = z.enum([
    "PENDING",
    "DISPATCHING",
    "DISPATCHED",
    "FAILED",
]);
export const fiscalResearchProviderSchema = z
    .object({
    kind: z.literal("VERTEX_GEMINI_GOOGLE_SEARCH"),
    model: z.string().trim().min(1).max(120),
    location: z.string().trim().min(1).max(80),
})
    .strict();
export const fiscalResearchCitationStatusSchema = z.enum([
    "QUEUED",
    "FETCHING",
    "INGESTED",
    "REJECTED",
]);
export const fiscalResearchCitationSchema = z
    .object({
    citationId: z.string().uuid(),
    sourceId: z.string().uuid(),
    sourceKey: z.string().trim().min(1).max(64),
    publisher: z.string().trim().min(1).max(160),
    jurisdiction: z.string().trim().min(1).max(80),
    url: z
        .string()
        .url()
        .max(2_000)
        .refine((value) => new URL(value).protocol === "https:", "must use HTTPS"),
    title: z.string().trim().min(1).max(1_000),
    snippet: z.string().max(2_000),
    status: fiscalResearchCitationStatusSchema,
    sourceRunId: z.string().uuid().nullable(),
    evidenceId: z.string().uuid().nullable(),
    rejectionReason: z.string().trim().max(240).nullable(),
})
    .strict();
export const fiscalResearchCitationUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    environment: environmentSchema,
    taskId: z.string().uuid(),
    citationId: z.string().uuid(),
    correlationId: z.string().trim().min(1).max(128),
    idempotencyKey: z.string().trim().min(1).max(200),
    status: z.enum(["FETCHING", "INGESTED", "REJECTED"]),
    evidenceId: z.string().uuid().nullable(),
    rejectionReason: z.string().trim().min(1).max(240).nullable().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.status === "FETCHING" && value.evidenceId !== null) {
        context.addIssue({
            code: "custom",
            path: ["evidenceId"],
            message: "FETCHING citations cannot have an evidenceId",
        });
    }
    if (value.status === "INGESTED" && value.evidenceId === null) {
        context.addIssue({
            code: "custom",
            path: ["evidenceId"],
            message: "INGESTED citations require an evidenceId",
        });
    }
    if (value.status === "REJECTED" && value.evidenceId !== null) {
        context.addIssue({
            code: "custom",
            path: ["evidenceId"],
            message: "REJECTED citations cannot have an evidenceId",
        });
    }
    if (value.status === "REJECTED" && !value.rejectionReason) {
        context.addIssue({
            code: "custom",
            path: ["rejectionReason"],
            message: "REJECTED citations require a rejectionReason",
        });
    }
    if (value.status !== "REJECTED" &&
        value.rejectionReason !== undefined &&
        value.rejectionReason !== null) {
        context.addIssue({
            code: "custom",
            path: ["rejectionReason"],
            message: "Only REJECTED citations may have a rejectionReason",
        });
    }
});
export const fiscalResearchCountersSchema = z
    .object({
    candidateCount: z.number().int().nonnegative(),
    allowlistedCount: z.number().int().nonnegative(),
    queuedCount: z.number().int().nonnegative(),
    ingestedCount: z.number().int().nonnegative(),
    rejectedCount: z.number().int().nonnegative(),
})
    .strict();
const fiscalResearchErrorSchema = z
    .object({
    code: z.string().trim().min(1).max(80),
    message: z.string().trim().min(1).max(1_000),
    retryable: z.boolean(),
})
    .strict();
const fiscalResearchDispatchSchema = z
    .object({
    status: fiscalResearchDispatchStateSchema,
    providerRequestId: z.string().trim().min(1).max(500).nullable(),
    dispatchedAt: z.string().datetime().nullable(),
})
    .strict();
export const fiscalResearchTaskCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    taskType: fiscalResearchTaskTypeSchema,
    query: z.string().trim().min(3).max(500),
    sourceIds: z.array(z.string().uuid()).min(1).max(10),
    maxDocuments: z.number().int().min(1).max(10).default(5),
    reason: z.string().trim().min(10).max(1_000),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.sourceIds).size !== value.sourceIds.length) {
        context.addIssue({
            code: "custom",
            path: ["sourceIds"],
            message: "sourceIds must be unique",
        });
    }
});
export const fiscalResearchTaskSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    taskType: fiscalResearchTaskTypeSchema,
    status: fiscalResearchTaskStateSchema,
    environment: environmentSchema,
    organizationId: z.string().uuid(),
    requestId: z.string().min(1).max(128),
    correlationId: z.string().min(1).max(128),
    query: z.string().min(3).max(500),
    reason: z.string().min(10).max(1_000),
    sourceIds: z.array(z.string().uuid()).min(1).max(10),
    maxDocuments: z.number().int().min(1).max(10),
    provider: fiscalResearchProviderSchema,
    dispatch: fiscalResearchDispatchSchema,
    searchQueries: z.array(z.string().trim().min(1).max(500)).max(10),
    summary: z.string().max(6_000).nullable(),
    citations: z.array(fiscalResearchCitationSchema).max(25),
    counters: fiscalResearchCountersSchema,
    lastError: fiscalResearchErrorSchema.nullable(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
    revision: z.number().int().positive(),
})
    .strict()
    .superRefine((task, context) => {
    if (task.counters.allowlistedCount !==
        task.citations.filter((item) => item.status !== "REJECTED").length) {
        context.addIssue({
            code: "custom",
            path: ["counters", "allowlistedCount"],
            message: "allowlistedCount must match non-rejected citations",
        });
    }
    if (task.counters.queuedCount !== task.citations.filter((item) => item.status === "QUEUED").length) {
        context.addIssue({
            code: "custom",
            path: ["counters", "queuedCount"],
            message: "queuedCount must match queued citations",
        });
    }
    if (task.counters.ingestedCount !==
        task.citations.filter((item) => item.status === "INGESTED").length) {
        context.addIssue({
            code: "custom",
            path: ["counters", "ingestedCount"],
            message: "ingestedCount must match ingested citations",
        });
    }
    // Rejected grounding candidates are intentionally not persisted as
    // citations because they have no trusted source identity. The counter is
    // the audit-friendly count of candidates discarded by the allowlist.
});
export const fiscalResearchTaskPageSchema = cursorPageSchema
    .extend({
    items: z.array(fiscalResearchTaskSchema),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
export const fiscalResearchRetrySchema = z
    .object({ reason: z.string().trim().min(10).max(1000) })
    .strict();
export const FISCAL_RESEARCH_DISPATCH_LEASE_MS = 5 * 60 * 1000;
export function canRetryFiscalResearchDispatch(task, now = Date.now()) {
    return ((task.status === "WAITING_REVIEW" ||
        (task.status === "FAILED" && task.lastError?.retryable === true)) &&
        task.createdBy !== null &&
        task.citations.some((citation) => citation.status === "QUEUED") &&
        task.citations.every((citation) => citation.status !== "QUEUED" || citation.sourceRunId !== null) &&
        (!["PENDING", "DISPATCHING"].includes(task.dispatch.status) ||
            now - Date.parse(task.updatedAt) >= FISCAL_RESEARCH_DISPATCH_LEASE_MS));
}
//# sourceMappingURL=fiscal-research.js.map